<template>
  <dashboard-widget
    :is-loading-spinner="false"
    :is-loading="isLoading">
    <template>
      <div class="row gutter-sm">
        <div class="col-xs-2 col-sm-2 col-md-2 col-lg-1 icon">
          <img class="align-center" src="../../../../../assets/leads-count-blue-icon.svg" />
        </div>
        <div class="col-xs-10 col-sm-10 col-md-10 col-lg-2">
          <h2>{{ leads }}</h2>
          <p>{{ $t('ui.dashboard.leads_collected_label') }}</p>
        </div>

        <div class="col-xs-2 col-sm-2 col-md-2 col-lg-1">
          <pie-chart
            v-if="isCharts"
            :colors="['#BF0711', '#3a3e50']"
            :values="charts.hot.absolute === 0 ? [0, 100] : [charts.hot.relative, 100 - 100 * (charts.hot.absolute/leads)]"
            :labels="['Residential', 'Non-Residential']"
          />
        </div>
        <div class="col-xs-4 col-sm-4 col-md-4 col-lg-1">
          <h4>{{ charts.hot.absolute }}</h4>
          <p>{{ $t('ui.dashboard.hot') }}</p>
        </div>

        <div class="col-xs-2 col-sm-2 col-md-2 col-lg-1">
          <pie-chart
            v-if="isCharts"
            :rotation="percentageToDegrees(charts.hot.relative)"
            :colors="['#9C6F19', '#3a3e50']"
            :values="charts.warm.absolute === 0 ? [0, 100] : [charts.warm.relative, 100 - 100 * (charts.warm.absolute/leads)]"
            :labels="['Residential', 'Non-Residential']"
          />
        </div>
        <div class="col-xs-4 col-sm-4 col-md-4 col-lg-1">
          <h4>{{ charts.warm.absolute }}</h4>
          <p>{{ $t('ui.dashboard.warm') }}</p>
        </div>

        <div class="col-xs-2 col-sm-2 col-md-2 col-lg-1">
          <pie-chart
            v-if="isCharts"
            :rotation="percentageToDegrees(charts.warm.relative + charts.hot.relative)"
            :colors="['#00828E', '#3a3e50']"
            :values="charts.cold.absolute === 0 ? [0, 100] : [charts.cold.relative, 100 - 100 * (charts.cold.absolute/leads)]"
            :labels="['Residential', 'Non-Residential']"
          />
        </div>
        <div class="col-xs-4 col-sm-4 col-md-4 col-lg-1">
          <h4>{{ charts.cold.absolute }}</h4>
          <p>{{ $t('ui.dashboard.cold') }}</p>
        </div>

        <div class="col-xs-2 col-sm-2 col-md-2 col-lg-1">
          <pie-chart
            v-if="isCharts"
            :rotation="percentageToDegrees(charts.warm.relative + charts.hot.relative + charts.cold.relative)"
            :colors="['#9198AB', '#3a3e50']"
            :values="charts.unclassified.absolute === 0 ? [0, 100] : [charts.unclassified.relative, 100 - 100 * (charts.unclassified.absolute/leads)]"
            :labels="['Residential', 'Non-Residential']"
          />
        </div>
        <div class="col-xs-4 col-sm-4 col-md-4 col-lg-1">
          <h4>{{ charts.unclassified.absolute }}</h4>
          <p>{{ $t('ui.dashboard.unclassified') }}</p>
        </div>
      </div>
    </template>
  </dashboard-widget>
</template>

<script>
import sum from 'lodash/sum'
import eq from 'lodash/eq'
import round from 'lodash/round'
import assign from 'lodash/assign'

import Charts from '@/api/Charts.js'
import PieChart from '../types/PieChart'
import DashboardWidget from './../DashboardWidget'
import WidgetMixin from './mixins/WidgetMixin'
import WidgetPeriodMixin from './mixins/WidgetPeriodMixin'
import WidgetQueryMixin from './mixins/WidgetQueryMixin'

const getDefaultCharts = () => ({
  hot: {
    absolute: 0,
    relative: 0
  },

  warm: {
    absolute: 0,
    relative: 0
  },

  cold: {
    absolute: 0,
    relative: 0
  },

  unclassified: {
    absolute: 0,
    relative: 0
  }
})

export default {
  components:  {
    DashboardWidget,
    PieChart
  },

  mixins: [WidgetMixin, WidgetPeriodMixin, WidgetQueryMixin],

  props: {
    identifier: {
      type: String,
      default: 'leads-collected'
    }
  },

  data: () => ({
    leads: 0,
    charts: getDefaultCharts()
  }),

  computed: {
    // Determinates if values are ready to be plotted
    isCharts: {
      get () {
        return !this.isLoading
      }
    }
  },

  methods: {
    async refresh() {
        this.setIsLoading(true)

        try {
            this.charts = getDefaultCharts()
            const leadsOverTime = await this.get(this.from(this.filter.period), this.filter.projectId)
            const { leads, ...charts } = this.transform(leadsOverTime)

            this.charts = charts
            this.leads = leads
        } catch (error) {
            this.setError(error)
            this.setIsError(true)
        } finally {
            this.setIsLoading(false)
            this.eventBus.$emit('widget-loaded', this.identifier)
        }
    },

    async get (from, project) {
      return new Promise(async (resolve, reject) => {
        let initial = {
            hot: 0,
            warm: 0,
            cold: 0,
            unclassified: 0
        }

        let charts = new Charts()

        let tuples = [
          { key: 'from', value: `\"${from}\"` }
        ]

        if (!!project) {
          tuples.push({ key: 'projectId', value: project })
        }

        const query = this.getQuery(tuples, 'classificationCounts')
        let { classification_counts: leadsCollected } = await charts.postQuery({
          organization_id: this.filter.organizationId,
          operationName: null,
          variables: {},
          query: `query Query {
              ${query}
            }`
        })
        .catch(reject)

        leadsCollected = assign(initial, leadsCollected)

        resolve(leadsCollected)
      })
    },

    transform: ({ cold, warm, hot, unclassified}) => ({
      cold: {
        absolute: cold ? cold : 0,
        relative: 100 * (cold/sum([cold, warm, hot, unclassified])) ? 100 * (cold/sum([cold, warm, hot, unclassified])) : 0
      },

      warm: {
        absolute: warm ? warm : 0,
        relative: 100 * (warm/sum([cold, warm, hot, unclassified])) ? 100 * (warm/sum([cold, warm, hot, unclassified])) : 0
      },

      hot: {
        absolute: hot ? hot : 0,
        relative: 100 * (hot/sum([cold, warm, hot, unclassified])) ? 100 * (hot/sum([cold, warm, hot, unclassified])) : 0
      },

      unclassified: {
        absolute: unclassified ? unclassified : 0,
        relative: 100 * (unclassified/sum([cold, warm, hot, unclassified])) ? 100 * (unclassified/sum([cold, warm, hot, unclassified])) : 0
      },

      leads: sum([cold, warm, hot, unclassified])
    }),

    // E. g. 50 % equals 180 °
    percentageToDegrees: percentage => round(((percentage * 360) / 100), 0)
  }
}
</script>

<style lang="sass" scoped>
  h2, h4
    color: #f9fafb
    margin-top: 0
    margin-bottom: 0

  p
    margin-bottom: 0
    color: #9198ab
</style>



// WEBPACK FOOTER //
// LeadsCollectedWidget.vue?aad4d58e